import { DotLegalDialog, DotLegalSearchField, DotLegalTable, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { InternalAssessmentApiRow } from "../InternalAssessments.types";
import { Alert, Box, Radio, RadioGroup, TableCell, TableRow, Typography } from "@mui/material";
import { Trans } from "react-i18next";
import { useTranslation } from "../../localization/useTranslation";
import { useChangeAssigneeDialogHooks } from "./ChangeAssigneeDialog.hooks";
import { SelectableAssignee } from "../SelectableAssignee.types";
import { getAssigneeRole } from "../SelectableAssignee.utils";
import CreateExternalUserInlineSection from "../../externalUser/createExternalUserInlineForm/CreateExternalUserInlineSection";

export interface IChangeAssigneeDialog {
    internalAssessmentViewModel: InternalAssessmentApiRow;
    onDialogClose: () => void;
    onChangedAssignee: () => void;
}

function ChangeAssigneeDialog(props: IChangeAssigneeDialog) {
    const { translateString, translateDate } = useTranslation();
    const {
        data,
        isLoading,
        assignee,
        isChanging,
        setAssignee,
        onChangeAssigneeClick,
        canChangeAssignee,
        assigneeSearch,
        setAssigneeSearch,
        canCreateRecipients,
        existingAssigneeEmails,
        onRecipientCreated,
    } = useChangeAssigneeDialogHooks(props);

    const getHeaders = () => {
        const headers = new Array<ITableHeader<SelectableAssignee>>();
        headers.push({ property: "id", text: "", align: "center", width: "50px" });
        headers.push({ property: "name", text: translateString("recipient") });
        headers.push({ property: "contactTypes", text: translateString("role") });
        headers.push({ property: "email", text: translateString("email") });
        return headers;
    };

    return (
        <DotLegalDialog
            size="md"
            buttonOkText={translateString("save")}
            open
            onDialogClose={props.onDialogClose}
            onOkClick={onChangeAssigneeClick}
            okButtonDisabled={!canChangeAssignee}
            isLoading={isLoading}
            header={translateString("changeAssignee")}
            inProgress={isChanging}
        >
            <Box display="flex" gap={1} flexDirection={"column"}>
                <Typography>
                    <Alert severity={"info"}>
                        <Trans
                            i18nKey={"changeAssigneeDialogDescription"}
                            values={{
                                audit: props.internalAssessmentViewModel.name,
                                deadline: translateDate(props.internalAssessmentViewModel.deadline),
                            }}
                            components={{
                                strong: <Box component="span" sx={(theme) => ({ fontWeight: "bold", color: theme.palette.primary.dark })}></Box>,
                            }}
                        />
                    </Alert>
                </Typography>

                <Box display="flex" justifyContent="space-between" alignItems={"center"} flexDirection={"row-reverse"}>
                    <DotLegalSearchField labelSearch={translateString("search")} value={assigneeSearch} onChange={(value) => setAssigneeSearch(value)} />
                </Box>

                {canCreateRecipients && <CreateExternalUserInlineSection existingEmails={existingAssigneeEmails} onCreated={onRecipientCreated} />}

                <RadioGroup value={assignee} onChange={(event, value) => setAssignee(value)}>
                    <DotLegalTable
                        data={data ?? []}
                        headers={getHeaders()}
                        defaultOrderBy={"name"}
                        defaultOrder={"asc"}
                        isLoading={isLoading}
                        clickableRows
                        searchString={assigneeSearch}
                        emptyText={translateString("noData")}
                        renderRow={(row) => (
                            <TableRow key={row.id} onClick={() => setAssignee(row.id)}>
                                <TableCell>
                                    <Radio value={row.id} />
                                </TableCell>
                                <TableCell>{row.name}</TableCell>
                                <TableCell>{getAssigneeRole(row, translateString)}</TableCell>
                                <TableCell>{row.email}</TableCell>
                            </TableRow>
                        )}
                        paginationLabelOf={translateString("labelOf")}
                    />
                </RadioGroup>
            </Box>
        </DotLegalDialog>
    );
}

export default ChangeAssigneeDialog;
